import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { IBPProfile } from '@type_definitions/datatypes';
import { BPProfilesService } from '../../bp-profiles.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { LovService } from '@app/shared/services/lov.service';
import { util } from '@app/shared/util';
import { BPProfilesDataService } from '../../bp-profiles-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { AuthService } from '@app/shared/services/auth/auth.service';

@Component({
  selector: 'bp-profiles-general-info',
  template: `
    <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
    <formly [form]="form" [model]="model" [fields]="fields" *ngIf="!loading"></formly>
  `
})
export class BPProfilesGeneralInfoComponent extends BaseBPProfilesDetailComponent implements OnInit, OnDestroy {
  public model:IBPProfile = <any>{};

  private numberOfStaffField:IExtraFieldConfigurationObject;
  private annualRevenueField:IExtraFieldConfigurationObject;
  private currencyField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private bpProfilesDataService:BPProfilesDataService,
    private translationService:TranslationService,
    private lovService:LovService,
    private authService:AuthService,
    protected bpProfilesService:BPProfilesService
  ) {
    super(bpProfilesService);
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const isEditingMode:boolean = this.bpProfilesService.editingMode;
    const labelClass:string = 'col-sm-3';
    const valueClass:string = 'col-sm-9';
    const isACSLUser:boolean = this.authService.checkAcslUser();

    return [
      this.formlyService.aValue('companyGroup')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.aField(isEditingMode? FormlyType.INPUT : FormlyType.VALUE, 'taxNumber')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.aField(isEditingMode? FormlyType.DATE_INPUT : FormlyType.VALUE, 'businessStarted')
        .withFormat(this.formattingService.formatDateMedium)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.aField(isEditingMode? FormlyType.INPUT : FormlyType.VALUE, 'businessRegistrationNr')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.numberOfStaffField = this.formlyService.aField(isEditingMode? FormlyType.SELECT : FormlyType.VALUE, 'numberOfStaff')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.annualRevenueField = this.formlyService.aField(isEditingMode? FormlyType.SELECT : FormlyType.VALUE, 'annualRevenuesMUSD')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.currencyField = this.formlyService.aField(isEditingMode && isACSLUser? FormlyType.SELECT : FormlyType.VALUE, 'contractCurrency')
        .withFormat(this.formattingService.formatCurrencyL)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.aField(isEditingMode? FormlyType.INPUT : FormlyType.VALUE, 'companyCountryRegisteredName')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      this.formlyService.aField(isEditingMode? FormlyType.INPUT : FormlyType.VALUE, 'companyCountryNickname')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build()
    ];
  }

  protected fillFields():void {
    // Populate the field 'Number of Staff'
    this.formlyService.fillField(this.numberOfStaffField, this.lovService.list('DOSTAFF'));

    // Populate the field 'Annual Revenue'
    this.formlyService.fillField(this.annualRevenueField, this.lovService.list('DOREVEN'));

    // Populate the field 'Currency'
    this.formlyService.fillField(this.currencyField, this.lovService.list(`localcurrency-${this.bpProfilesService.activeProfile?.country?.id}`));
  }

  protected onSave = ():void => {
    const data:IBPProfile = util.copyAndExtend(this.bpProfilesService.profileDetail, this.model);

    // convert start date to timestamp value in ms
    if (data.businessStarted) {
      data.businessStarted = parseInt(this.formattingService.formatDate(data.businessStarted, 'x'));
    }

    this.setSaveBusy(true);
    this.bpProfilesDataService.updateBPProfileDetail(this.bpProfilesService.activeProfile?.id, data, this.translationService.instant('BPPROFILES.GENERAL_INFO.TEXT'))
      .then((profileDetail:IBPProfile) => {
        this.bpProfilesService.profileDetail = profileDetail;
      })
      .catch(util.noop)
      .finally(() => {
        this.setSaveBusy(false);
      });
  };
}